import { useEffect, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { GameSectionsContent } from '~/domains/common/components/GameSectionsList/GameSectionsList';

import { PROMOTED_MARKETS_PARAMS } from '../../hooks/usePromotedMarkets';
import { useLoadedMarketsPusher } from '../../hooks/useLoadedMarketsPusher';
import { useMarketGroupVisibility } from '../../hooks/useMarketGroupVisibility';
import { reportPredictionsWidgetViewed } from '../../analyticsEvents';

import { AllPredictionsLink } from './AllPredictionsLink';
import { CarouselSkeleton } from './CarouselSkeleton';
import { MarketsCarousel } from './MarketsCarousel';
import { PromotedMarketCard } from './PromotedMarketCard';
import { readTapContext } from './readTapContext';
import { WIDGET_LOCATION } from './constants';

type PromotedMarketsSectionProps = {
  marketGroupIds: string[];
  isLoading: boolean;
  isVisible: boolean;
};

/** Split from the gate so a hidden widget pays for neither the pusher nor the observer. */
export function PromotedMarketsSection({
  marketGroupIds,
  isLoading,
  isVisible,
}: PromotedMarketsSectionProps) {
  const { t } = useTranslation('market');
  const observeMarketGroup = useMarketGroupVisibility();
  const hasReportedRenderRef = useRef(false);

  useLoadedMarketsPusher({ types: PROMOTED_MARKETS_PARAMS.types, enabled: isVisible });

  useEffect(() => {
    if (isLoading || hasReportedRenderRef.current) return;

    const [leadMarketGroupId] = marketGroupIds;
    const lead = leadMarketGroupId ? readTapContext(leadMarketGroupId) : undefined;

    reportPredictionsWidgetViewed({
      location: WIDGET_LOCATION,
      featuredGameId: lead?.gameId,
      featuredLeague: lead?.league,
      tickerGameCount: marketGroupIds.length,
    });
    hasReportedRenderRef.current = true;
  }, [isLoading, marketGroupIds]);

  return (
    <GameSectionsContent
      fullWidth
      sections={[
        {
          key: 'promoted-markets',
          title: t('homeWidget.title'),
          headerActions: <AllPredictionsLink />,
          items: marketGroupIds,
        },
      ]}
      renderSectionItems={(ids) => {
        if (isLoading) return <CarouselSkeleton />;

        if (ids.length === 1) {
          return (
            <PromotedMarketCard
              marketGroupId={ids[0]}
              placement="featured"
              observeMarketGroup={observeMarketGroup}
            />
          );
        }

        return <MarketsCarousel marketGroupIds={ids} observeMarketGroup={observeMarketGroup} />;
      }}
    />
  );
}
